import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output } from '@angular/core';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { CalendarType } from '@modules/calendar-app/types/calendar-type';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { DateTimeUtils } from '@modules/common/utils/DateTimeUtils';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { isSameDay } from '@sharedlib/common/utils/date';

/** What the row shows between its arrows: a weekday with its day square, or a plain label. */
type DateNavLabel = { kind: 'day'; weekday: string; day: string; today: boolean } | { kind: 'text'; text: string };

/**
 * The date row under a calendar's header, with the ‹ › arrows around the period on show:
 * "‹ Sunday [1] ›" (filled square on today, outline on another day), "‹ Nov 1 – 7 ›",
 * "‹ November 2026 ›" or "‹ 2026 ›". Johanna, 2026-11-01, after the prototype's Calendar.
 */
@Component({
  selector: 'stch-calendar-date-nav',
  templateUrl: './calendar-date-nav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button2Component, IconComponent],
})
export class CalendarDateNavComponent {
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly localeId = inject(LOCALE_ID);

  public readonly view = input.required<CalendarType>();
  /** Any instant inside the period on show. */
  public readonly date = input.required<Date>();

  public readonly previous = output<void>();
  public readonly next = output<void>();

  protected readonly label = computed((): DateNavLabel | null => {
    const view = this.view();
    const date = this.date();
    if (!view || !date) {
      return null;
    }

    // The grid lays days out on the account's wall clock, so the row reckons and formats on it too.
    const timeZone = this.displayTimeZoneService.timeZone();
    const wallClock = toDisplayZoneWallClock(date, timeZone);
    const from = DateTimeUtils.beginningOfPeriod(wallClock, view, this.localeId);
    switch (view) {
      case CalendarType.DAY:
        return {
          kind: 'day',
          weekday: this.formatted(from, 'EEEE'),
          day: this.formatted(from, 'd'),
          today: isSameDay(from, toDisplayZoneWallClock(new Date(), timeZone)),
        };
      case CalendarType.WEEK:
      case CalendarType.WORKWEEK:
        return { kind: 'text', text: this.rangeText(from, DateTimeUtils.endOfPeriod(wallClock, view, this.localeId)) };
      case CalendarType.MONTH:
        return { kind: 'text', text: this.formatted(from, 'MMMM y') };
      default:
        return { kind: 'text', text: this.formatted(from, 'y') };
    }
  });

  protected readonly previousLabel = computed(() => `Previous ${this.unitName()}`);
  protected readonly nextLabel = computed(() => `Next ${this.unitName()}`);

  private readonly unitName = computed((): string => {
    switch (this.view()) {
      case CalendarType.DAY:
        return 'day';
      case CalendarType.MONTH:
        return 'month';
      case CalendarType.YEAR:
        return 'year';
      default:
        return 'week';
    }
  });

  /** "Nov 1 – 7", or "Oct 29 – Nov 4" across a month's end. */
  private rangeText(from: Date, to: Date): string {
    const toText = from.getMonth() === to.getMonth() ? this.formatted(to, 'd') : this.formatted(to, 'MMM d');
    return `${this.formatted(from, 'MMM d')} – ${toText}`;
  }

  private formatted(wallClock: Date, pattern: string): string {
    return formatDate(wallClock, pattern, this.localeId);
  }
}
